import React, { useState, useEffect } from 'react';

// Fade In Animation Hook
export const useFadeIn = (delay: number = 0) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return isVisible;
};

// Staggered Animation Hook
export const useStaggeredAnimation = (itemCount: number, baseDelay: number = 100) => {
  const [visibleItems, setVisibleItems] = useState<boolean[]>(new Array(itemCount).fill(false));

  useEffect(() => {
    const timers = visibleItems.map((_, index) => 
      setTimeout(() => {
        setVisibleItems(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, index * baseDelay)
    );

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [itemCount, baseDelay]);

  return visibleItems;
};

// Animated Container Component
interface AnimatedContainerProps {
  children: React.ReactNode;
  animation?: 'fadeIn' | 'slideUp' | 'slideDown' | 'slideLeft' | 'slideRight' | 'scale' | 'stagger';
  delay?: number;
  duration?: number;
  className?: string;
  staggerDelay?: number;
}

export const AnimatedContainer: React.FC<AnimatedContainerProps> = ({
  children,
  animation = 'fadeIn',
  delay = 0,
  duration = 300,
  className = '',
  staggerDelay = 100
}) => {
  const isVisible = useFadeIn(delay);

  const getAnimationClasses = () => {
    const baseClasses = 'transition-all duration-300 ease-out';
    
    if (!isVisible) {
      switch (animation) {
        case 'fadeIn':
          return `${baseClasses} opacity-0`;
        case 'slideUp':
          return `${baseClasses} opacity-0 translate-y-4`;
        case 'slideDown':
          return `${baseClasses} opacity-0 -translate-y-4`;
        case 'slideLeft':
          return `${baseClasses} opacity-0 translate-x-4`;
        case 'slideRight':
          return `${baseClasses} opacity-0 -translate-x-4`;
        case 'scale':
          return `${baseClasses} opacity-0 scale-95`;
        default:
          return `${baseClasses} opacity-0`;
      }
    } else {
      return `${baseClasses} opacity-100 translate-y-0 translate-x-0 scale-100`;
    }
  };

  if (animation === 'stagger') {
    return (
      <StaggeredList delay={delay} staggerDelay={staggerDelay} className={className}>
        {children}
      </StaggeredList>
    );
  }

  return (
    <div className={`${getAnimationClasses()} ${className}`}>
      {children}
    </div>
  );
};

// Staggered List Component
interface StaggeredListProps {
  children: React.ReactNode;
  delay?: number;
  staggerDelay?: number;
  className?: string;
}

export const StaggeredList: React.FC<StaggeredListProps> = ({
  children,
  delay = 0,
  staggerDelay = 100,
  className = ''
}) => {
  const childrenArray = React.Children.toArray(children);
  const visibleItems = useStaggeredAnimation(childrenArray.length, staggerDelay);

  useEffect(() => {
    // Start the stagger animation after initial delay
    const timer = setTimeout(() => {
      // Animation logic is handled by the hook
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div className={className}>
      {childrenArray.map((child, index) => (
        <div
          key={index}
          className={`transition-all duration-300 ease-out ${
            visibleItems[index] 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-2'
          }`}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

// Hover Animation Component
interface HoverAnimationProps {
  children: React.ReactNode;
  animation?: 'lift' | 'scale' | 'glow' | 'slide' | 'rotate';
  intensity?: 'subtle' | 'medium' | 'strong';
  className?: string;
}

export const HoverAnimation: React.FC<HoverAnimationProps> = ({
  children,
  animation = 'lift',
  intensity = 'medium',
  className = ''
}) => {
  const getHoverClasses = () => {
    const baseClasses = 'transition-all duration-200 ease-in-out cursor-pointer';
    
    switch (animation) {
      case 'lift':
        const liftIntensity = {
          subtle: 'hover:-translate-y-0.5 hover:shadow-md',
          medium: 'hover:-translate-y-1 hover:shadow-lg',
          strong: 'hover:-translate-y-2 hover:shadow-xl'
        };
        return `${baseClasses} ${liftIntensity[intensity]}`;
        
      case 'scale':
        const scaleIntensity = {
          subtle: 'hover:scale-101',
          medium: 'hover:scale-105',
          strong: 'hover:scale-110'
        };
        return `${baseClasses} ${scaleIntensity[intensity]}`;
        
      case 'glow':
        const glowIntensity = {
          subtle: 'hover:shadow-md hover:shadow-primary-200',
          medium: 'hover:shadow-lg hover:shadow-primary-300',
          strong: 'hover:shadow-xl hover:shadow-primary-400'
        };
        return `${baseClasses} ${glowIntensity[intensity]}`;
        
      case 'slide':
        return `${baseClasses} hover:translate-x-1`;
        
      case 'rotate':
        const rotateIntensity = {
          subtle: 'hover:rotate-1',
          medium: 'hover:rotate-2',
          strong: 'hover:rotate-3'
        };
        return `${baseClasses} ${rotateIntensity[intensity]}`;
        
      default:
        return baseClasses;
    }
  };

  return (
    <div className={`${getHoverClasses()} ${className}`}>
      {children}
    </div>
  );
};

// Pulse Animation Component
interface PulseProps {
  children: React.ReactNode;
  color?: 'primary' | 'secondary' | 'green' | 'red' | 'amber';
  size?: 'sm' | 'md' | 'lg';
  speed?: 'slow' | 'normal' | 'fast';
  className?: string;
}

export const Pulse: React.FC<PulseProps> = ({
  children,
  color = 'primary',
  size = 'md',
  speed = 'normal',
  className = ''
}) => {
  const colorClasses = {
    primary: 'shadow-primary-400',
    secondary: 'shadow-secondary-400',
    green: 'shadow-green-400',
    red: 'shadow-red-400',
    amber: 'shadow-amber-400'
  };

  const sizeClasses = {
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg'
  };

  const speedClasses = {
    slow: 'animate-pulse [animation-duration:2s]',
    normal: 'animate-pulse',
    fast: 'animate-pulse [animation-duration:0.5s]'
  };

  return (
    <div className={`${speedClasses[speed]} ${sizeClasses[size]} ${colorClasses[color]} ${className}`}>
      {children}
    </div>
  );
};

// Loading Animation Component
interface LoadingAnimationProps {
  type?: 'spinner' | 'dots' | 'bars' | 'wave';
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'white';
  className?: string;
}

export const LoadingAnimation: React.FC<LoadingAnimationProps> = ({
  type = 'spinner',
  size = 'md',
  color = 'primary',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  const colorClasses = {
    primary: 'text-primary-600',
    secondary: 'text-secondary-600',
    white: 'text-white'
  };

  if (type === 'dots') {
    return (
      <div className={`flex space-x-1 ${className}`}>
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className={`${sizeClasses[size]} ${colorClasses[color]} bg-current rounded-full animate-pulse`}
            style={{
              animationDelay: `${index * 0.15}s`,
              animationDuration: '0.6s'
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'bars') {
    return (
      <div className={`flex items-end space-x-1 ${className}`}>
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className={`w-1 ${colorClasses[color]} bg-current animate-pulse`}
            style={{
              height: `${12 + (index % 2) * 8}px`,
              animationDelay: `${index * 0.1}s`,
              animationDuration: '0.8s'
            }}
          />
        ))}
      </div>
    );
  }

  if (type === 'wave') {
    return (
      <div className={`flex items-center space-x-1 ${className}`}>
        {[0, 1, 2, 3, 4].map((index) => (
          <div
            key={index}
            className={`w-1 h-8 ${colorClasses[color]} bg-current rounded-full animate-pulse`}
            style={{
              animationDelay: `${index * 0.1}s`,
              animationDuration: '1.2s',
              transform: `scaleY(${0.4 + Math.sin(index * 0.5) * 0.6})`
            }}
          />
        ))}
      </div>
    );
  }

  // Default spinner
  return (
    <div
      className={`animate-spin rounded-full border-2 border-gray-200 border-t-current ${sizeClasses[size]} ${colorClasses[color]} ${className}`}
    />
  );
};

// Page Transition Component
interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
}

export const PageTransition: React.FC<PageTransitionProps> = ({
  children,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div 
      className={`transition-all duration-300 ease-out ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-2'
      } ${className}`}
    >
      {children}
    </div>
  );
};

export default AnimatedContainer;
